import { expr, stmt, type PineStmt } from "./ast";

// Template bodies are split so the visual condition composer (A2) can swap
// out the signal expressions without losing the indicator/plot setup.
export type PineTemplateBody = {
  setup: PineStmt[];
  longSignal: PineStmt;
  shortSignal: PineStmt;
};

export type PineStrategyTemplateId =
  | "ema_trend"
  | "rsi_reversion"
  | "opening_range_breakout"
  | "supertrend_pullback"
  | "macd_cross"
  | "bollinger_reversion"
  | "donchian_breakout"
  | "adx_filtered_trend";

export type PineStrategyCategory =
  | "trend"
  | "mean-reversion"
  | "breakout"
  | "volatility";

export type PineStrategyComplexity = "starter" | "intermediate" | "advanced";

export type PineStrategyTemplate = {
  id: PineStrategyTemplateId;
  name: string;
  summary: string;
  defaultFastLength: number;
  defaultSlowLength: number;
  defaultRiskReward: number;
  defaultStopLossPct: number;
  category: PineStrategyCategory;
  complexity: PineStrategyComplexity;
  /**
   * Free-text guidance on where the template fits best. Surfaced in the
   * template library UI so users can pick by intent, not by name.
   */
  compatibility: string;
};

export const PINE_STRATEGY_TEMPLATES: PineStrategyTemplate[] = [
  {
    id: "ema_trend",
    name: "EMA Trend Follow",
    summary:
      "Fast/slow EMA crossover with ATR-backed stop and risk-reward target.",
    defaultFastLength: 9,
    defaultSlowLength: 21,
    defaultRiskReward: 2,
    defaultStopLossPct: 1,
    category: "trend",
    complexity: "starter",
    compatibility:
      "Works on liquid futures, indices, and major FX. Best on 15m-1h timeframes during clear trending sessions.",
  },
  {
    id: "rsi_reversion",
    name: "RSI Mean Reversion",
    summary: "RSI oversold/overbought reversals filtered by trend direction.",
    defaultFastLength: 14,
    defaultSlowLength: 200,
    defaultRiskReward: 1.5,
    defaultStopLossPct: 0.8,
    category: "mean-reversion",
    complexity: "starter",
    compatibility:
      "Range-bound assets and post-news fade plays. Avoid during strong directional moves where the trend filter will rarely allow entries.",
  },
  {
    id: "opening_range_breakout",
    name: "Opening Range Breakout",
    summary:
      "Breakout of a configurable opening range with fixed risk controls.",
    defaultFastLength: 30,
    defaultSlowLength: 14,
    defaultRiskReward: 2,
    defaultStopLossPct: 1.2,
    category: "breakout",
    complexity: "intermediate",
    compatibility:
      "Intraday equity futures and tier-1 FX. Validate session timing matches the exchange-of-record before going live.",
  },
  {
    id: "supertrend_pullback",
    name: "Supertrend Pullback",
    summary:
      "Trend pullback entries using Supertrend direction and RSI confirmation.",
    defaultFastLength: 10,
    defaultSlowLength: 3,
    defaultRiskReward: 2,
    defaultStopLossPct: 1,
    category: "trend",
    complexity: "intermediate",
    compatibility:
      "Strong-trending instruments on 1h-4h. Performs poorly in chop where Supertrend whips repeatedly.",
  },
  {
    id: "macd_cross",
    name: "MACD Cross",
    summary:
      "Classic MACD line crosses signal line, optionally filtered by zero-line.",
    defaultFastLength: 12,
    defaultSlowLength: 26,
    defaultRiskReward: 2,
    defaultStopLossPct: 1.5,
    category: "trend",
    complexity: "starter",
    compatibility:
      "Mid-frequency trend signals on liquid stocks/ETFs. Lagging by design; tune fast/slow lengths to match the asset's typical cycle.",
  },
  {
    id: "bollinger_reversion",
    name: "Bollinger Mean Reversion",
    summary:
      "Long when close pierces lower band, short when it pierces upper band, with regime filter.",
    defaultFastLength: 20,
    defaultSlowLength: 200,
    defaultRiskReward: 1.5,
    defaultStopLossPct: 0.9,
    category: "mean-reversion",
    complexity: "intermediate",
    compatibility:
      "Stationary instruments and pair trades. Disable during regime-change days (CPI, FOMC); bands expand and the strategy chases.",
  },
  {
    id: "donchian_breakout",
    name: "Donchian Channel Breakout",
    summary:
      "Long when close breaks above N-bar high, short when it breaks below N-bar low. Turtle-style.",
    defaultFastLength: 20,
    defaultSlowLength: 10,
    defaultRiskReward: 2,
    defaultStopLossPct: 1.5,
    category: "breakout",
    complexity: "starter",
    compatibility:
      "Commodities, large-cap stocks, and trending FX. Donchian widows you out during chop - keep position size small.",
  },
  {
    id: "adx_filtered_trend",
    name: "ADX-Filtered EMA Trend",
    summary:
      "EMA crossover entries gated by ADX above a strength threshold. Skips chop.",
    defaultFastLength: 12,
    defaultSlowLength: 26,
    defaultRiskReward: 2,
    defaultStopLossPct: 1.2,
    category: "trend",
    complexity: "advanced",
    compatibility:
      "Any instrument where trend strength matters. ADX threshold of 20-25 typically filters out the worst chop. Backtest on at least 5 years.",
  },
];

export const TEMPLATE_IDS = new Set(
  PINE_STRATEGY_TEMPLATES.map((t) => t.id),
);

// Shared expression shortcuts to keep template seeds readable.
const close = expr.id("close");
const high = expr.id("high");
const low = expr.id("low");
const time = expr.id("time");
const fastLengthId = expr.id("fastLength");
const slowLengthId = expr.id("slowLength");

function plotLine(
  source: string,
  label: string,
  color: ReturnType<typeof expr.call>,
): PineStmt {
  return stmt.callStmt(
    "plot",
    [expr.id(source), expr.str(label)],
    [
      ["color", color],
      ["linewidth", expr.num(2)],
    ],
  );
}

function colorNew(swatch: string): ReturnType<typeof expr.call> {
  return expr.call("color.new", [
    expr.member("color", swatch),
    expr.num(0),
  ]) as ReturnType<typeof expr.call>;
}

function emaTrendBody(): PineTemplateBody {
  return {
    setup: [
      stmt.assign("fast", expr.call("ta.ema", [close, fastLengthId])),
      stmt.assign("slow", expr.call("ta.ema", [close, slowLengthId])),
      plotLine("fast", "Fast EMA", colorNew("lime")),
      plotLine("slow", "Slow EMA", colorNew("orange")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.call("ta.crossover", [expr.id("fast"), expr.id("slow")]),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.call("ta.crossunder", [expr.id("fast"), expr.id("slow")]),
    ),
  };
}

function rsiReversionBody(): PineTemplateBody {
  const trendId = expr.id("trend");
  const rsiId = expr.id("rsi");
  return {
    setup: [
      stmt.assign("rsi", expr.call("ta.rsi", [close, fastLengthId])),
      stmt.assign("trend", expr.call("ta.ema", [close, slowLengthId])),
      plotLine("trend", "Trend EMA", colorNew("blue")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.bin(
        "and",
        expr.bin(">", close, trendId),
        expr.call("ta.crossover", [rsiId, expr.num(30)]),
      ),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.bin(
        "and",
        expr.bin("<", close, trendId),
        expr.call("ta.crossunder", [rsiId, expr.num(70)]),
      ),
    ),
  };
}

function openingRangeBreakoutBody(): PineTemplateBody {
  const rangeStart = expr.id("rangeStart");
  const rangeHigh = expr.id("rangeHigh");
  const rangeLow = expr.id("rangeLow");
  const rangeMinutes = expr.id("rangeMinutes");
  const inRange = expr.id("inRange");
  return {
    setup: [
      stmt.assign("rangeMinutes", fastLengthId),
      stmt.assign("atrLength", slowLengthId),
      stmt.assign(
        "newSession",
        expr.bin(
          "!=",
          expr.call("ta.change", [expr.call("time", [expr.str("D")])]),
          expr.num(0),
        ),
      ),
      stmt.assign("rangeHigh", expr.na(), "var float"),
      stmt.assign("rangeLow", expr.na(), "var float"),
      stmt.assign("rangeStart", expr.na(), "var int"),
      stmt.ifBlock(expr.id("newSession"), [
        stmt.reassign("rangeHigh", high),
        stmt.reassign("rangeLow", low),
        stmt.reassign("rangeStart", time),
      ]),
      stmt.assign(
        "inRange",
        expr.bin(
          "and",
          expr.unary("not", expr.call("na", [rangeStart])),
          expr.bin(
            "<=",
            expr.bin("-", time, rangeStart),
            expr.bin(
              "*",
              expr.bin("*", rangeMinutes, expr.num(60)),
              expr.num(1000),
            ),
          ),
        ),
      ),
      stmt.ifBlock(inRange, [
        stmt.reassign("rangeHigh", expr.call("math.max", [rangeHigh, high])),
        stmt.reassign("rangeLow", expr.call("math.min", [rangeLow, low])),
      ]),
      plotLine("rangeHigh", "Opening Range High", colorNew("lime")),
      plotLine("rangeLow", "Opening Range Low", colorNew("red")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.bin(
        "and",
        expr.unary("not", inRange),
        expr.call("ta.crossover", [close, rangeHigh]),
      ),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.bin(
        "and",
        expr.unary("not", inRange),
        expr.call("ta.crossunder", [close, rangeLow]),
      ),
    ),
  };
}

function supertrendPullbackBody(): PineTemplateBody {
  const direction = expr.id("direction");
  const supertrend = expr.id("supertrend");
  const rsi = expr.id("rsi");
  return {
    setup: [
      stmt.assign("atrLength", fastLengthId),
      stmt.assign("factor", slowLengthId),
      stmt.assign(
        ["supertrend", "direction"],
        expr.call("ta.supertrend", [expr.id("factor"), expr.id("atrLength")]),
      ),
      stmt.assign("rsi", expr.call("ta.rsi", [close, expr.num(14)])),
      stmt.callStmt(
        "plot",
        [supertrend, expr.str("Supertrend")],
        [
          [
            "color",
            expr.tern(
              expr.bin("<", direction, expr.num(0)),
              expr.member("color", "lime"),
              expr.member("color", "red"),
            ),
          ],
          ["linewidth", expr.num(2)],
        ],
      ),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.bin(
        "and",
        expr.bin(
          "and",
          expr.bin("<", direction, expr.num(0)),
          expr.bin(">", close, supertrend),
        ),
        expr.call("ta.crossover", [rsi, expr.num(50)]),
      ),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.bin(
        "and",
        expr.bin(
          "and",
          expr.bin(">", direction, expr.num(0)),
          expr.bin("<", close, supertrend),
        ),
        expr.call("ta.crossunder", [rsi, expr.num(50)]),
      ),
    ),
  };
}

function macdCrossBody(): PineTemplateBody {
  const macdLine = expr.id("macdLine");
  const signalLine = expr.id("signalLine");
  return {
    setup: [
      // Pine v5 macd: [macd, signal, hist] = ta.macd(close, fast, slow, 9)
      stmt.assign(
        ["macdLine", "signalLine", "macdHist"],
        expr.call("ta.macd", [
          close,
          fastLengthId,
          slowLengthId,
          expr.num(9),
        ]),
      ),
      plotLine("macdLine", "MACD", colorNew("aqua")),
      plotLine("signalLine", "MACD Signal", colorNew("orange")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.call("ta.crossover", [macdLine, signalLine]),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.call("ta.crossunder", [macdLine, signalLine]),
    ),
  };
}

function bollingerReversionBody(): PineTemplateBody {
  const basis = expr.id("bbBasis");
  const upper = expr.id("bbUpper");
  const lower = expr.id("bbLower");
  const trend = expr.id("bbTrend");
  return {
    setup: [
      stmt.assign("bbBasis", expr.call("ta.sma", [close, fastLengthId])),
      stmt.assign("bbDev", expr.call("ta.stdev", [close, fastLengthId])),
      stmt.assign("bbUpper", expr.bin("+", basis, expr.bin("*", expr.num(2), expr.id("bbDev")))),
      stmt.assign("bbLower", expr.bin("-", basis, expr.bin("*", expr.num(2), expr.id("bbDev")))),
      stmt.assign("bbTrend", expr.call("ta.ema", [close, slowLengthId])),
      plotLine("bbUpper", "BB Upper", colorNew("orange")),
      plotLine("bbLower", "BB Lower", colorNew("orange")),
      plotLine("bbTrend", "Regime EMA", colorNew("aqua")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.bin(
        "and",
        expr.bin(">", close, trend),
        expr.call("ta.crossover", [close, lower]),
      ),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.bin(
        "and",
        expr.bin("<", close, trend),
        expr.call("ta.crossunder", [close, upper]),
      ),
    ),
  };
}

function donchianBreakoutBody(): PineTemplateBody {
  const upper = expr.id("donHigh");
  const lower = expr.id("donLow");
  return {
    setup: [
      // Use [1] reference so the prior bar's extreme is the trigger,
      // avoiding repaint where the current bar's value becomes its own break.
      stmt.assign(
        "donHigh",
        expr.call("ta.highest", [
          expr.call("ta.valuewhen", [
            expr.bool(true),
            expr.id("high"),
            expr.num(1),
          ]),
          fastLengthId,
        ]),
      ),
      stmt.assign(
        "donLow",
        expr.call("ta.lowest", [
          expr.call("ta.valuewhen", [
            expr.bool(true),
            expr.id("low"),
            expr.num(1),
          ]),
          fastLengthId,
        ]),
      ),
      plotLine("donHigh", "Donchian High", colorNew("lime")),
      plotLine("donLow", "Donchian Low", colorNew("red")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.call("ta.crossover", [close, upper]),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.call("ta.crossunder", [close, lower]),
    ),
  };
}

function adxFilteredTrendBody(): PineTemplateBody {
  // Pine v5 ADX: ta.adx(diLength, adxLength). We use slowLength as the
  // ADX strength threshold input (typical 20-25).
  return {
    setup: [
      stmt.assign(
        "adxValue",
        expr.call("ta.adx", [expr.num(14), expr.num(14)]),
      ),
      stmt.assign("adxThreshold", slowLengthId),
      stmt.assign("fastTrend", expr.call("ta.ema", [close, fastLengthId])),
      stmt.assign(
        "slowTrend",
        expr.call("ta.ema", [close, expr.bin("*", fastLengthId, expr.num(3))]),
      ),
      plotLine("adxValue", "ADX", colorNew("yellow")),
      plotLine("fastTrend", "Fast EMA", colorNew("lime")),
      plotLine("slowTrend", "Slow EMA", colorNew("orange")),
    ],
    longSignal: stmt.assign(
      "longSignal",
      expr.bin(
        "and",
        expr.bin(">", expr.id("adxValue"), expr.id("adxThreshold")),
        expr.call("ta.crossover", [
          expr.id("fastTrend"),
          expr.id("slowTrend"),
        ]),
      ),
    ),
    shortSignal: stmt.assign(
      "shortSignal",
      expr.bin(
        "and",
        expr.bin(">", expr.id("adxValue"), expr.id("adxThreshold")),
        expr.call("ta.crossunder", [
          expr.id("fastTrend"),
          expr.id("slowTrend"),
        ]),
      ),
    ),
  };
}

export function templateBody(
  templateId: PineStrategyTemplateId,
): PineTemplateBody {
  switch (templateId) {
    case "ema_trend":
      return emaTrendBody();
    case "rsi_reversion":
      return rsiReversionBody();
    case "opening_range_breakout":
      return openingRangeBreakoutBody();
    case "supertrend_pullback":
      return supertrendPullbackBody();
    case "macd_cross":
      return macdCrossBody();
    case "bollinger_reversion":
      return bollingerReversionBody();
    case "donchian_breakout":
      return donchianBreakoutBody();
    case "adx_filtered_trend":
      return adxFilteredTrendBody();
  }
}

export function templateBodyAst(templateId: PineStrategyTemplateId): PineStmt[] {
  const body = templateBody(templateId);
  return [...body.setup, body.longSignal, body.shortSignal];
}
